import {
  SupplierRelationshipFulfillmentType,
  SupplierGetResponseType,
  SupplierStorefrontV2GetResponseType,
} from "@sellar-io/api-v2/types";
import { SpinnerLegacy } from "../../../components";
import { CollectionOptionsCard } from "../../../components/fulfillmentTypeCards/CollectionOptionsCard";
import { CourierDeliveryOptionsCard } from "../../../components/fulfillmentTypeCards/CourierDeliveryOptionsCard";
import { DirectDeliveryOptionsCard } from "../../../components/fulfillmentTypeCards/DirectDeliveryOptionsCard";
import { HaulierDeliveryOptionsCard } from "../../../components/fulfillmentTypeCards/HaulierDeliveryOptionsCard";
import {
  RadioGroup,
  RadioGroupOption,
  RadioGroupContainer,
} from "../../../components/radioSelector/RadioGroup";
import { ContextOrderBasketType } from "../../../hooks";
import { useFulfillmentTypeList } from "../../../hooks/api/v2/fulfillmentType/useFulfillmentTypeList";
import { useFulfillmentTypeEligibility } from "../../../hooks/useFulfillmentTypeEligibility";
import { useFulfillmentTypeAutoSelect } from "../../../hooks/useFulfillmentTypeAutoSelect";
import { LegacyOrderCheckoutType } from "../checkoutV2/lib/types";
import { useFeatureFlagStore } from "../../../hooks/useFeatureFlags";
import { useIneligibleForCourierStore } from "../../../hooks/useIneligibleForCourierStore";
import { CourierUnavailableSelector } from "./CourierUnavailableSelector";
import { HaulierTierInfoCard } from "./HaulierTierInfoCard";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import { DistributionDeliveryOptionsCard } from "../../../components/fulfillmentTypeCards/DistributionDeliveryOptionsCard";

import { CheckoutSelectedFulfillmentType } from "..";
import { twMerge } from "tailwind-merge";

type CheckoutOrderType = {
  OrderItems: { Product: { availableFrom: Date | null; id: number } }[];
};

type CompositeOrderType =
  | ContextOrderBasketType
  | LegacyOrderCheckoutType
  | CheckoutOrderType
  | null
  | undefined;

export const FulfillmentTypeRadioSelector = ({
  value,
  onUpdate,
  supplier,
  connection,
  order,
  supplierStorefront,
  buyerPostcode,
}: {
  value: CheckoutSelectedFulfillmentType;
  onUpdate: (value: CheckoutSelectedFulfillmentType) => void;
  supplier: SupplierGetResponseType;
  connection:
    | { fulfillmentType: SupplierRelationshipFulfillmentType | null }
    | undefined
    | null;
  order: CompositeOrderType;
  supplierStorefront: SupplierStorefrontV2GetResponseType | undefined;
  buyerPostcode: string;
}) => {
  const { hasItemIneligibleForCourier } = useIneligibleForCourierStore();
  const sdDeliveryArea = supplierStorefront?.sdDeliveryArea;
  const directDeliveryArea = supplierStorefront?.directDeliveryArea;

  const palletCalculationsEnabled =
    useFeatureFlagStore().isActive("PALLET_OVERRIDE");

  const palletOverrideEnabled = useRemoteFeatureFlags().isActive(
    "courierHaulierEnforcement",
  );

  const eligibility = useFulfillmentTypeEligibility({
    allowCollectionOnCheckout: supplier.allowCollectionOnCheckout,
    connection,
    directDeliveryArea,
    sdDeliveryArea,
    eligibleForHaulierOverride:
      hasItemIneligibleForCourier && palletOverrideEnabled,
  });

  const {
    isEligibleForCollection,
    isEligibleForCourier,
    isEligibleForDirect,
    isEligibleForHaulier,
    isEligibleForDistribution,
  } = eligibility;

  const fulfillmentTypeCourierQuery = useFulfillmentTypeList(
    {
      query: [
        {
          type: "SupplierId",
          value: supplier.id,
          modifier: "EQUALS",
        },
        {
          type: "FulfillmentType",
          value: "courier",
          modifier: "EQUALS",
        },
      ],
      sortBy: "createdAt",
    },
    {
      enabled: isEligibleForCourier,
    },
  );

  const fulfillmentTypeDirectQuery = useFulfillmentTypeList(
    {
      query: [
        {
          type: "SupplierId",
          value: supplier.id,
          modifier: "EQUALS",
        },
        {
          type: "FulfillmentType",
          value: "direct",
          modifier: "EQUALS",
        },
      ],
      sortBy: "createdAt",
    },
    {
      enabled: isEligibleForDirect,
    },
  );

  const fulfillmentTypeDistributionQuery = useFulfillmentTypeList(
    {
      query: [
        {
          type: "SupplierId",
          value: supplier.id,
          modifier: "EQUALS",
        },
        {
          type: "FulfillmentType",
          value: "sellarDistribution",
          modifier: "EQUALS",
        },
      ],
    },
    {
      enabled: isEligibleForDistribution,
    },
  );

  const fulfillmentTypeHaulierQuery = useFulfillmentTypeList(
    {
      query: [
        {
          type: "SupplierId",
          value: supplier.id,
          modifier: "EQUALS",
        },
        {
          type: "FulfillmentType",
          value: "haulier",
          modifier: "EQUALS",
        },
      ],
      sortBy: "createdAt",
    },
    {
      enabled: isEligibleForHaulier || palletOverrideEnabled,
    },
  );

  const fulfillmentTypeCourierQueryLoading =
    fulfillmentTypeCourierQuery.isPending && isEligibleForCourier;
  const fulfillmentTypeHaulierQueryLoading =
    fulfillmentTypeHaulierQuery.isPending &&
    (isEligibleForHaulier || palletOverrideEnabled) &&
    !isEligibleForDistribution;

  const courierFulfillmentType =
    fulfillmentTypeCourierQuery.data?.pages[0]?.[0];

  const haulierFulfillmentType =
    fulfillmentTypeHaulierQuery.data?.pages[0]?.[0];

  const directFulfillmentType = fulfillmentTypeDirectQuery.data?.pages[0]?.[0];

  const distributionFulfillmentType =
    fulfillmentTypeDistributionQuery.data?.pages[0]?.[0];

  const unConnectedValue = isEligibleForDirect ? "direct" : "courier";
  const initialValue = isEligibleForDistribution
    ? "sellarDistribution"
    : (connection?.fulfillmentType ?? unConnectedValue);

  const disableCourier =
    hasItemIneligibleForCourier &&
    haulierFulfillmentType &&
    palletOverrideEnabled;

  const loading =
    fulfillmentTypeCourierQueryLoading || fulfillmentTypeHaulierQueryLoading;

  useFulfillmentTypeAutoSelect({
    isEligibleForDistribution,
    isEligibleForDirect,
    value,
    supplierId: supplier.id,
    onUpdate,
  });

  if (loading) return <SpinnerLegacy />;

  return (
    <>
      <RadioGroup
        value={value ?? initialValue}
        onChange={(value) => {
          onUpdate(value as CheckoutSelectedFulfillmentType);
        }}
      >
        <RadioGroupContainer>
          {isEligibleForDistribution && (
            <RadioGroupOption
              value={"sellarDistribution"}
              radioClassName={twMerge(
                value === "sellarDistribution" && "bg-[#27272A]",
              )}
              className="p-4"
            >
              <DistributionDeliveryOptionsCard
                deliveryArea={sdDeliveryArea}
                supplier={supplier}
                directFulfillmentType={distributionFulfillmentType}
                orderItems={order?.OrderItems}
                buyerPostcode={buyerPostcode}
              />
            </RadioGroupOption>
          )}
          {isEligibleForDirect && (
            <RadioGroupOption value={"direct"} className={"p-4"}>
              <DirectDeliveryOptionsCard
                deliveryArea={directDeliveryArea}
                supplier={supplier}
                directFulfillmentType={directFulfillmentType}
                orderItems={order?.OrderItems}
              />
            </RadioGroupOption>
          )}
          {isEligibleForCourier && (
            <>
              {disableCourier ? (
                <CourierUnavailableSelector
                  updateFulfillmentType={() => onUpdate("courier")}
                />
              ) : (
                <RadioGroupOption value={"courier"} className={"p-4"}>
                  <CourierDeliveryOptionsCard
                    supplier={supplier}
                    courierFulfillmentType={courierFulfillmentType}
                    orderItems={order?.OrderItems}
                  />
                </RadioGroupOption>
              )}
            </>
          )}
          {(isEligibleForHaulier || !!haulierFulfillmentType?.charge) && (
            <RadioGroupOption value={"haulier"} className={"p-4"}>
              <HaulierDeliveryOptionsCard
                supplierId={supplier.id}
                haulierFulfillmentType={haulierFulfillmentType}
              />
            </RadioGroupOption>
          )}
          {isEligibleForCollection && (
            <RadioGroupOption value={"collection"} className={"p-4"}>
              <CollectionOptionsCard
                supplier={supplier}
                orderItems={order?.OrderItems}
              />
            </RadioGroupOption>
          )}
        </RadioGroupContainer>
      </RadioGroup>
      {palletCalculationsEnabled &&
        (value === "haulier" || initialValue === "haulier") && (
          <HaulierTierInfoCard
            supplierStorefront={supplierStorefront}
            fulfillmentType={haulierFulfillmentType}
          />
        )}
    </>
  );
};
